import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { onAdminRefresh } from '../utils/adminSocket';
import API from '../utils/api';
import { T } from '../adminTheme';
import { NAV_GROUPS as GROUPS, DASHBOARD_ITEM } from '../adminNav';
import { useNavTip } from './NavTip';
import { ChevronDown, ChevronUp, Globe, LogOut, ShieldCheck, X } from 'lucide-react';

// the public website: localhost while developing, the real site everywhere else
const WEBSITE_URL = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:3000'
    : 'https://gettimelammatrimony.com';

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { admin, logout, mobileNavOpen, closeMobileNav, sidebarCollapsed } = useAdmin();
    const scrollRef = useRef(null);
    const navTip = useNavTip();

    useEffect(() => {
        const saved = sessionStorage.getItem('adminSidebarScroll');
        if (saved && scrollRef.current) scrollRef.current.scrollTop = Number(saved);
    }, []);
    const handleSidebarScroll = (e) => {
        sessionStorage.setItem('adminSidebarScroll', e.currentTarget.scrollTop);
    };

    const [pendingCount, setPendingCount] = useState(0);
    const [newMembersCount, setNewMembersCount] = useState(0);
    const [newProvidersCount, setNewProvidersCount] = useState(0);
    const [pendingStoriesCount, setPendingStoriesCount] = useState(0);
    const [pendingCategoriesCount, setPendingCategoriesCount] = useState(0);
    const [pendingProvidersCount, setPendingProvidersCount] = useState(0);
    const [unreadAlertsCount, setUnreadAlertsCount] = useState(0);
    const [unreadSupportCount, setUnreadSupportCount] = useState(0);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
    const [pendingAadharCount, setPendingAadharCount] = useState(0);
    const [incompleteRegistrationCount, setIncompleteRegistrationCount] = useState(0);

    useEffect(() => {
        const fetchCount = () => {
            API.get('/admin/options/pending-count')
                .then(res => setPendingCount(res.data.count || 0))
                .catch(() => { });
        };
        fetchCount();
        const interval = setInterval(fetchCount, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        let lastRun = 0;
        const fetchCounts = () => {
            // never more than one round of count requests every 2 seconds, whatever triggers it
            if (Date.now() - lastRun < 2000) return;
            lastRun = Date.now();
            API.get('/admin/new-users-count')
                .then(res => { setNewMembersCount(res.data.members || 0); setNewProvidersCount(res.data.providers || 0); })
                .catch(() => { });
            API.get('/admin/options/pending-count', { params: { category: 'servicecategory' } })
                .then(res => setPendingCategoriesCount(res.data.count || 0))
                .catch(() => { });
            API.get('/admin/vendors/pending-count')
                .then(res => setPendingProvidersCount(res.data.count || 0))
                .catch(() => { });
            API.get('/admin/alerts/unread-count')
                .then(res => setUnreadAlertsCount(res.data.count || 0))
                .catch(() => { });
            API.get('/testimonials/admin/pending-count')
                .then(res => setPendingStoriesCount(res.data.count || 0))
                .catch(() => { });
            API.get('/admin/aadhar/pending-count')
                .then(res => setPendingAadharCount(res.data.count || 0))
                .catch(() => { });
            API.get('/admin/messages/unread-count')
                .then(res => setUnreadMessagesCount(res.data.count || 0))
                .catch(() => { });
            API.get('/support/admin/unread-count')
                .then(res => setUnreadSupportCount(res.data.count || 0))
                .catch(() => { });
            API.get('/admin/registration-drafts/count')
                .then(res => setIncompleteRegistrationCount(res.data.count || 0))
                .catch(() => { });
        };
        fetchCounts();
        const interval = setInterval(fetchCounts, 30000);
        window.addEventListener('usersSeen', fetchCounts);
        window.addEventListener('messagesSeen', fetchCounts);
        const offRefresh = onAdminRefresh(fetchCounts); // realtime: a new message or sign-up updates the badges at once
        return () => {
            clearInterval(interval);
            window.removeEventListener('usersSeen', fetchCounts);
            window.removeEventListener('messagesSeen', fetchCounts);
            offRefresh();
        };
    }, []);

    const badgeFor = (path) => {
        if (path === '/profile-options' && pendingCount > 0) return pendingCount;
        if (path === '/users' && newMembersCount > 0) return newMembersCount;
        if (path === '/service-providers' && newProvidersCount > 0) return newProvidersCount;
        if (path === '/testimonials' && pendingStoriesCount > 0) return pendingStoriesCount;
        if (path === '/service-categories' && pendingCategoriesCount > 0) return pendingCategoriesCount;
        if (path === '/services' && pendingProvidersCount > 0) return pendingProvidersCount;
        if (path === '/notifications' && unreadAlertsCount > 0) return unreadAlertsCount;
        if (path === '/support-chat' && unreadSupportCount > 0) return unreadSupportCount;
        if (path === '/messages' && unreadMessagesCount > 0) return unreadMessagesCount;
        if (path === '/profiles' && pendingAadharCount > 0) return pendingAadharCount;
        if (path === '/incomplete-registrations' && incompleteRegistrationCount > 0) return incompleteRegistrationCount;
        return 0;
    };

    const groupBadge = (group) => group.items.reduce((sum, item) => sum + badgeFor(item.path), 0);

    const activeGroupId = GROUPS.find(g => g.items.some(i => i.path === location.pathname))?.id;
    const [openGroups, setOpenGroups] = useState(() => new Set(activeGroupId ? [activeGroupId] : []));
    const toggleGroup = (id) => setOpenGroups(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // Collapsed desktop rail — clicking a group icon opens a flyout with
    // that group's items instead of navigating straight to one of them.
    const [flyoutGroup, setFlyoutGroup] = useState(null);

    const go = (path) => { navigate(path); closeMobileNav(); setFlyoutGroup(null); };

    // Shared full grouped-list markup — used by both the expanded desktop
    // sidebar and the mobile floating panel, so badge counts and open/close
    // state stay in sync without fetching twice.
    const renderFullNavList = () => (
        <>
            <div style={styles.sectionLabel}>MENU</div>
            <div
                style={{ ...styles.menuItem, ...(location.pathname === '/dashboard' ? styles.menuItemActive : {}) }}
                onClick={() => go('/dashboard')}>
                {location.pathname === '/dashboard' && <span style={styles.activeRail} />}
                <DASHBOARD_ITEM.Icon size={18} color={location.pathname === '/dashboard' ? T.primary : T.textSecondary} />
                <span>Dashboard</span>
            </div>

            {GROUPS.map(group => {
                const isOpen = openGroups.has(group.id);
                const groupHasActive = group.id === activeGroupId;
                return (
                    <div key={group.id} style={{ marginTop: '4px' }}>
                        <div
                            style={{ ...styles.groupHeader, color: groupHasActive ? T.primary : T.textSecondary }}
                            onClick={() => toggleGroup(group.id)}>
                            <span>{group.label}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                {groupBadge(group) > 0 && <span style={{ ...styles.pendingBadge, marginLeft: 0 }} title="Total waiting in this section">{groupBadge(group)}</span>}
                                {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                            </span>
                        </div>
                        {isOpen && (
                            <div style={styles.groupItems}>
                                {group.items.map(item => {
                                    const active = location.pathname === item.path;
                                    const badge = badgeFor(item.path);
                                    return (
                                        <div key={item.path} onMouseEnter={(e) => navTip.onEnter(e, item.label)} onMouseLeave={navTip.onLeave}
                                            style={{ ...styles.menuItem, ...(active ? styles.menuItemActive : {}) }}
                                            onClick={() => go(item.path)}>
                                            {active && <span style={styles.activeRail} />}
                                            <item.Icon size={17} color={active ? T.primary : T.textSecondary} style={{ flexShrink: 0 }} />
                                            <span data-nav-label style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                                            {badge > 0 && <span style={styles.pendingBadge}>{badge}</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            <div style={styles.menuItem}
                onClick={() => window.open(WEBSITE_URL, '_blank')}>
                <Globe size={17} color={T.textSecondary} />
                <span>View Website</span>
            </div>
        </>
    );

    return (
        <>
            {navTip.node}
            {/* ── Desktop sidebar (full or collapsed rail) ── */}
            <div className="admin-sidebar" style={styles.sidebar} ref={scrollRef} onScroll={handleSidebarScroll}>
                <div style={styles.logo}>
                    <div style={styles.logoMark}>
                        <ShieldCheck size={19} color="#fff" strokeWidth={2.3} />
                    </div>
                    {!sidebarCollapsed && <div style={styles.logoText}>Gettimelam</div>}
                </div>

                {sidebarCollapsed ? (
                    <div style={styles.railScroll} onClick={() => setFlyoutGroup(null)}>
                        <div
                            style={{ ...styles.railItem, ...(location.pathname === '/dashboard' ? styles.railItemActive : {}) }}
                            title="Dashboard"
                            onClick={(e) => { e.stopPropagation(); go('/dashboard'); }}>
                            <DASHBOARD_ITEM.Icon size={19} color={location.pathname === '/dashboard' ? T.primary : T.textSecondary} />
                        </div>
                        {GROUPS.map(group => {
                            const groupHasActive = group.id === activeGroupId;
                            const isFlyoutOpen = flyoutGroup === group.id;
                            return (
                                <div key={group.id} style={{ position: 'relative' }}>
                                    <div
                                        style={{ ...styles.railItem, ...(groupHasActive || isFlyoutOpen ? styles.railItemActive : {}) }}
                                        title={group.label}
                                        onClick={(e) => { e.stopPropagation(); setFlyoutGroup(isFlyoutOpen ? null : group.id); }}>
                                        <group.Icon size={19} color={groupHasActive || isFlyoutOpen ? T.primary : T.textSecondary} />
                                        {groupBadge(group) > 0 && <span style={styles.railBadge}>{groupBadge(group)}</span>}
                                    </div>
                                    {isFlyoutOpen && (
                                        <div style={styles.flyout} onClick={e => e.stopPropagation()}>
                                            <div style={styles.flyoutTitle}>{group.label}</div>
                                            {group.items.map(item => {
                                                const active = location.pathname === item.path;
                                                const badge = badgeFor(item.path);
                                                return (
                                                    <div key={item.path} onMouseEnter={(e) => navTip.onEnter(e, item.label)} onMouseLeave={navTip.onLeave}
                                                        style={{ ...styles.flyoutItem, ...(active ? { color: T.primary, background: T.primaryPale } : {}) }}
                                                        onClick={() => go(item.path)}>
                                                        <item.Icon size={16} color={active ? T.primary : T.textSecondary} style={{ flexShrink: 0 }} />
                                                        <span data-nav-label style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                                                        {badge > 0 && <span style={styles.pendingBadge}>{badge}</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <div style={styles.railItem} title="View Website"
                            onClick={(e) => { e.stopPropagation(); window.open(WEBSITE_URL, '_blank'); }}>
                            <Globe size={19} color={T.textSecondary} />
                        </div>
                    </div>
                ) : (
                    <div className="admin-menu-scroll" style={styles.menuScroll}>{renderFullNavList()}</div>
                )}

                <div style={{ ...styles.userCard, justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                    <div style={styles.userAvatar}>
                        {(admin?.name || 'A').charAt(0).toUpperCase()}
                        <span style={styles.onlineDot} />
                    </div>
                    {!sidebarCollapsed && (
                        <>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={styles.userName}>{admin?.name}</div>
                                <div style={styles.userEmail}>{admin?.email || 'Administrator'}</div>
                            </div>
                            <button style={styles.logoutBtn} title="Logout" onClick={logout}>
                                <LogOut size={16} color={T.dangerText} />
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* ── Mobile/tablet floating "Navigation" panel ── */}
            <div className={`admin-mobile-nav-overlay ${mobileNavOpen ? 'open' : ''}`} onClick={closeMobileNav}>
                <div style={styles.mobileCard} className="admin-mobile-nav-card" onClick={e => e.stopPropagation()}>
                    <div style={styles.mobileHeader}>
                        <h2 style={styles.mobileTitle}>Navigation</h2>
                        <button style={styles.mobileCloseBtn} onClick={closeMobileNav}><X size={20} color={T.textSecondary} /></button>
                    </div>
                    <div className="admin-menu-scroll" style={styles.mobileScroll}>{renderFullNavList()}</div>
                    <div style={styles.mobileFooter}>
                        <div style={styles.userAvatar}>{(admin?.name || 'A').charAt(0).toUpperCase()}<span style={styles.onlineDot} /></div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={styles.userName}>{admin?.name}</div>
                            <div style={styles.userEmail}>{admin?.email || 'Administrator'}</div>
                        </div>
                        <button style={styles.logoutBtn} title="Logout" onClick={logout}>
                            <LogOut size={16} color={T.dangerText} />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

const styles = {
    // display/flexDirection live in AdminLayout.css (.admin-sidebar), not
    // here — an inline style would always beat the CSS media query that
    // hides the sidebar below 1024px, since inline style wins over any
    // stylesheet rule regardless of specificity.
    sidebar: {
        background: T.sidebarBg,
        borderRight: `1px solid ${T.border}`,
        height: '100vh',
        position: 'fixed',
        left: 0, top: 0, bottom: 0,
        zIndex: 200,
        overflow: 'visible',
    },
    logo: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
        padding: '20px 16px 16px',
        flexShrink: 0,
    },
    logoMark: {
        width: '34px', height: '34px', borderRadius: '10px',
        background: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
    },
    logoText: { fontSize: '16px', fontWeight: '800', color: T.textPrimary, letterSpacing: '-0.2px', whiteSpace: 'nowrap' },

    menuScroll: { flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '4px 12px 12px' },
    sectionLabel: {
        fontSize: '11px', fontWeight: '700', color: T.textMuted, letterSpacing: '1px',
        textTransform: 'uppercase', padding: '10px 12px 6px',
    },
    groupHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px', fontSize: '12.5px', fontWeight: '700',
        letterSpacing: '0.3px', cursor: 'pointer', userSelect: 'none',
    },
    groupItems: { paddingLeft: '6px', borderLeft: `1.5px solid ${T.border}`, marginLeft: '18px' },
    menuItem: {
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: '11px',
        padding: '9px 12px', borderRadius: '9px',
        cursor: 'pointer', fontSize: '13.5px', fontWeight: '500',
        color: T.textSecondary,
        marginBottom: '2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: '100%',
    },
    menuItemActive: { background: T.primaryPale, color: T.primary, fontWeight: '600' },
    activeRail: {
        position: 'absolute', left: '-6px', top: '18%', bottom: '18%', width: '3px',
        borderRadius: '3px', background: T.primary,
    },
    pendingBadge: {
        marginLeft: 'auto', background: T.danger, color: '#fff',
        fontSize: '10.5px', fontWeight: '700', padding: '1px 7px',
        borderRadius: '10px', lineHeight: 1.5,
    },

    // ── Collapsed rail ──
    // No scroll here — overflow-y:auto would force Chromium to also clip
    // overflow-x (the two axes can't independently be auto/visible), which
    // would cut off the flyout popovers. The rail's ~8 icons always fit.
    railScroll: { flex: 1, padding: '4px 12px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
    railItem: {
        position: 'relative', width: '42px', height: '42px', borderRadius: '10px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0,
    },
    railItemActive: { background: T.primaryPale },
    railBadge: {
        position: 'absolute', top: '2px', right: '2px', minWidth: '16px', height: '16px', padding: '0 4px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
        background: T.danger, color: '#fff', fontSize: '9.5px', fontWeight: '700', borderRadius: '8px', lineHeight: 1,
    },
    flyout: {
        position: 'absolute', left: 'calc(100% + 10px)', top: '-6px', zIndex: 210,
        background: T.sidebarBg, border: `1px solid ${T.border}`, borderRadius: '12px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.14)', padding: '8px', minWidth: '200px',
    },
    flyoutTitle: { fontSize: '11px', fontWeight: '700', color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 10px 8px' },
    flyoutItem: {
        display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px',
        borderRadius: '8px', fontSize: '13px', fontWeight: '500', color: T.textPrimary,
        cursor: 'pointer', whiteSpace: 'nowrap',
    },

    userCard: {
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '14px 16px',
        borderTop: `1px solid ${T.border}`,
        flexShrink: 0,
    },
    userAvatar: {
        position: 'relative',
        width: '38px', height: '38px', borderRadius: '50%',
        background: T.primaryPale,
        color: T.primary, fontSize: '15px', fontWeight: '700',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
    },
    onlineDot: {
        position: 'absolute', bottom: '-1px', right: '-1px',
        width: '9px', height: '9px', borderRadius: '50%',
        background: T.success, border: `2px solid ${T.sidebarBg}`,
    },
    userName: { fontSize: '13px', fontWeight: '700', color: T.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    userEmail: { fontSize: '11px', color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex', flexShrink: 0, borderRadius: '8px' },

    // ── Mobile floating panel ──
    mobileCard: {
        background: T.sidebarBg, borderRadius: '16px', width: '320px', maxWidth: '100%',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
    },
    mobileHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 18px 12px', flexShrink: 0 },
    mobileTitle: { fontSize: '18px', fontWeight: '800', color: T.textPrimary, margin: 0 },
    mobileCloseBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', borderRadius: '8px' },
    mobileScroll: { flex: 1, overflowY: 'auto', padding: '0 12px 12px' },
    mobileFooter: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderTop: `1px solid ${T.border}`, flexShrink: 0 },
};

export default Sidebar;
